import type { CurrentUser } from '@/shared/api/generated/tenancy';

/* The mail domain the signed-in caller may add colleagues at.
 *
 * **In `entities/session` beside `can`, because it is the same kind of fact**: something derived
 * from who is signed in, read by a feature that wants to avoid offering a door that will slam. A
 * copy in `features/auth` would be one the Admin screen could not reach.
 *
 * **This hides nothing and protects nothing.** `TeamInviteService.add_user` calls
 * `refuse_a_foreign_domain` before it writes anything, on the actor's *verified token subject*, so
 * a request that skips this screen entirely is refused with a 422 that names the domain. What these
 * two functions buy is that an administrator is told the rule while typing rather than after
 * submitting — `can`'s reasoning, applied to a field instead of a button.
 *
 * The comparison mirrors `lumiq.shared.email_domains` deliberately and narrowly: case-folded,
 * whole-label, and `undefined` for anything that is not `local@domain`. It is not a second
 * implementation of the *rule* — the server owns that — it is the same question asked early. Where
 * the two could disagree, they disagree in the safe direction: this returns `undefined` and the
 * screen falls silent, leaving the server to answer.
 */

export function emailDomainOf(address: string): string | undefined {
  const at = address.trim().lastIndexOf('@');
  /* `lastIndexOf`, matching the backend's `rpartition`: a quoted local part may legally contain an
   * `@`, and the domain is always after the last one. */
  if (at <= 0) return undefined;
  const domain = address
    .trim()
    .slice(at + 1)
    .toLowerCase();
  /* A dot is required for the same reason the backend requires one — `sam@localhost` is not a mail
   * domain this platform can compare, and admitting it would let two unlike addresses match. */
  if (domain === '' || !domain.includes('.')) return undefined;
  return domain;
}

/** The domain a caller may add colleagues at, or `undefined` when it cannot be derived.
 *
 * `undefined` while the identity is still loading, and `undefined` for a subject that is not an
 * email — an OIDC deployment's `sub` can be an opaque pairwise id. Callers must read it as "say
 * nothing" rather than as "allow nothing": the server is the one that refuses, and a screen that
 * blocked submission on a value it could not compute would break the flow rather than guard it.
 */
export function invitableDomain(identity: CurrentUser | undefined): string | undefined {
  if (identity === undefined) return undefined;
  return emailDomainOf(identity.subject);
}

/** Whether this address is one the caller may add, given the domain they are bound to.
 *
 * Both arguments rather than reading the identity here, so the decision is a pure function of two
 * strings and is testable without a query client.
 */
export function isInvitableAddress(
  address: string,
  allowed: string | undefined,
): boolean {
  /* No derivable bound means this screen has no opinion — see `invitableDomain`. */
  if (allowed === undefined) return true;
  return emailDomainOf(address) === allowed;
}
